import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";

/**
 * Result screen of the Dynamics 365 OAuth flow. The backend exchanges the code itself and
 * redirects here with `?success=` (+ `?error=` on failure) — this page never talks to the
 * API, unlike the legacy HubSpot code-exchange callback.
 */
export const DynamicsCallback: FunctionComponent = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const success = searchParams.get("success");
	const error = searchParams.get("error");

	const isSuccess = success === "true";
	const status: "success" | "error" = isSuccess ? "success" : "error";
	const errorMessage =
		error || (!isSuccess ? "Something went wrong while connecting to Dynamics 365." : "");

	const [countdown, setCountdown] = useState(3);

	useEffect(() => {
		if (status !== "success") {
			return;
		}

		const timer = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					navigate(ROUTE_CONFIG.INTEGRATIONS_MSDYNAMICS);

					return 0;
				}

				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [status, navigate]);

	return (
		<div className="w-full h-screen flex items-center justify-center bg-gray-50 p-4">
			<div className="flex flex-col bg-white p-8 rounded-[20px] w-[480px] max-w-full shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center">
				{status === "success" && (
					<>
						<Icon className="w-[48px] h-[48px] mb-4 text-status-success" icon={faCircleCheck} />
						<div className="text-center text-md font-bold mb-2">
							Dynamics 365 Connected Successfully!
						</div>
						<div className="text-center text-ssm text-gray-500 mb-6">
							Your Dynamics 365 environment has been connected. Redirecting to settings in{" "}
							{countdown}s...
						</div>
						<div className="flex w-full max-w-[200px] justify-center">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title="Go to Settings"
								onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS_MSDYNAMICS)}
							/>
						</div>
					</>
				)}

				{status === "error" && (
					<>
						<Icon className="w-[48px] h-[48px] mb-4 text-status-error" icon={faCircleXmark} />
						<div className="text-center text-md font-bold mb-2">Connection Failed</div>
						<div className="text-center text-ssm text-gray-500 mb-6">
							{errorMessage ||
								"Something went wrong while connecting to Dynamics 365. Please try again."}
						</div>
						<div className="flex w-full max-w-[200px] justify-center">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title="Try Again"
								onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS_MSDYNAMICS)}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
